import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { toast } from 'sonner';
import { Shield, Mail, Loader2 } from 'lucide-react';
import MicrosoftLoginButton from './MicrosoftLoginButton';

const SECURADO_LOGO_PRIMARY = "https://customer-assets.emergentagent.com/job_streamhub-crm/artifacts/39apig25_Securado%20Logo-01.jpg";

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('microsoft');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const ssoError = searchParams.get('error');
    if (ssoError) {
      setError(ssoError);
      toast.error(ssoError);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e0dfd4] via-[#f5f5f5] to-[#e8e8ea] p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-[#800000]/5 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-[#86c881]/10 blur-3xl"></div>
      </div>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-6">
            <img 
              src={SECURADO_LOGO_PRIMARY} 
              alt="Securado" 
              className="w-auto mx-auto"
              style={{ height: '160px', maxHeight: '160px' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-[#800000] to-[#9a1919] items-center justify-center hidden mx-auto">
              <Shield className="w-14 h-14 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#333333]">Welcome back</CardTitle>
          <CardDescription className="text-gray-500">
            Sign in to Securado BI Tool
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Tab Switcher */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-5" data-testid="login-tab-switcher">
            <button
              type="button"
              onClick={() => { setActiveTab('microsoft'); setError(''); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'microsoft'
                  ? 'bg-white text-[#333] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="login-tab-microsoft"
            >
              <svg className="w-4 h-4" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Microsoft 365
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('email'); setError(''); }}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all duration-200 ${
                activeTab === 'email'
                  ? 'bg-white text-[#333] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="login-tab-email"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
          </div>

          {/* Microsoft Tab */}
          {activeTab === 'microsoft' && (
            <div className="space-y-4" data-testid="login-microsoft-panel">
              <MicrosoftLoginButton 
                onSuccess={() => {
                  toast.success('Microsoft login successful');
                  navigate('/dashboard');
                }}
                onError={(err) => {
                  setError(err.message || 'Microsoft login failed');
                  toast.error(err.message || 'Microsoft login failed');
                }}
              />
              <p className="text-xs text-center text-gray-400">
                Sign in with your organization's Microsoft 365 account
              </p>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-email-panel">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#333333]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="border-gray-300 focus:border-[#800000] focus:ring-[#800000]/20"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#333333]">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="border-gray-300 focus:border-[#800000] focus:ring-[#800000]/20"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#800000] to-[#9a1919] hover:from-[#9a1919] hover:to-[#b02020] text-white shadow-lg shadow-[#800000]/25 transition-all duration-200"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Signing in...</>
                ) : 'Sign in'}
              </Button>
            </form>
          )}

          {/* Error display */}
          {error && (
            <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2" data-testid="login-error-message">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {error}
            </div>
          )}
          
          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Protected by Securado's Digital Vaccine
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Powered by AI, ML, and Human Intelligence
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
